import type { CanvasMenuAction, ScreenAction } from './components/ScreenLayout.js';
import type { ActionDeSurface, MenuDeSurface, FiltresDeSurface } from './cadre-canvas.js';
export type { ActionDeSurface, EntreeDeRail, MenuDeSurface, FiltresDeSurface } from './cadre-canvas.js';

/**
 * Validation des contributions natives d'une surface : libelles courts,
 * ajout unique, gestes executables et ids uniques. Les commandes de navigation
 * et de choix ne comptent pas comme actions metier supplementaires.
 * Le hook commun verifie elements et alimente avant cette validation.
 * Les parametres keeps restent un adaptateur pour les appels historiques.
 */

/**
 * UNE ACTION DE LA RANGEE 3a POSEE PAR UNE SURFACE. C'est une `ScreenAction`
 * du gabarit, moins `brouillon` : la fiche vide (G6) suppose une porte et un
 * jeu, qu'une App Canvas n'a pas. Son `kind: 'add'` porte donc SON `onClick`
 * (creer le contenu et l'ouvrir), et c'est la seule place du produit ou un
 * ajout n'ouvre pas un brouillon : il n'y a rien ou le projeter.
 */


/**
 * UNE ENTREE DU MENU « ... » (3b) POSEE PAR UNE SURFACE : une action, plus
 * `checked` pour un interrupteur (« Archives »), rendu a l'encre quand il est
 * actif (G17).
 */
export type OutilDeSurface = Omit<CanvasMenuAction, 'brouillon'>;

/**
 * LE TITRE QU'UNE SURFACE POSE EN 2a (`poserTitre`), NORMALISE : espaces de
 * bord retires, vide ou `null` = repli sur le nom de l'app seul, plafond de
 * `TITRE_DE_SURFACE_MAX` caracteres (le h1 tronque a l'ecran, le `title` porte
 * le reste ; un titre de plusieurs centaines de caracteres est une donnee mal
 * placee, pas un titre). Ce titre PEUT venir d'une donnee (le nom du canal) :
 * G13 vise les libelles d'ACTION, 2a dit ou l'on est.
 */
export const TITRE_DE_SURFACE_MAX = 120;

export function titreDeSurface(titre: string | null | undefined): string | null {
  if (titre == null) return null;
  if (typeof titre !== 'string') throw new Error('[app-runtime] poserTitre attend une chaine ou null.');
  const propre = titre.trim().replace(/\s+/gu, ' ');
  if (propre === '') return null;
  return propre.length > TITRE_DE_SURFACE_MAX ? `${propre.slice(0, TITRE_DE_SURFACE_MAX - 1)}…` : propre;
}

/** « + Ajouter » et trois autres au plus (section 3.3 du gabarit : 3 par porte). */
export const ACTIONS_DE_SURFACE_MAX = 3;
/** Les gestes d'aide colles a droite de la rangee 3a (`ScreenAction.cote`). */
export const ACTIONS_A_DROITE_MAX = 2;

/** Trois mots au plus pour un libelle de la rangee 3a (regle G13). */
export const MOTS_DE_LIBELLE_MAX = 3;

function compterLesMots(libelle: string): number {
  return libelle.trim().split(/\s+/u).filter((mot) => mot !== '').length;
}

function refus(appId: string, zone: '3a' | '3b', message: string): never {
  throw new Error(`[app-runtime] ${appId}, rangee ${zone} : ${message}`);
}

/**
 * Verifie ce qu'une surface pose dans une zone de la rangee 3, et le rend tel
 * quel s'il est recevable. JETTE sinon, avec l'app, la zone et le nom fautif.
 *
 * `keeps` est le raccourci historique de la zone. Un tableau VIDE est
 * toujours recevable, meme sur une zone non gardee : c'est l'etat de depart et
 * celui d'une surface qui n'a rien a dire pour l'instant.
 */
export function verifierRangeeDeSurface<T extends ActionDeSurface>(options: {
  appId: string;
  zone: '3a' | '3b';
  keeps: readonly string[];
  entrees: ReadonlyArray<T>;
}): ReadonlyArray<T> {
  const { appId, zone, keeps, entrees } = options;
  if (entrees.length === 0) return entrees;
  if (!keeps.includes(zone)) {
    refus(
      appId,
      zone,
      `la fiche ne garde pas ${zone} (canvas.app.keeps) : le gabarit ne rendrait pas ces gestes. ` +
        `Declarer "keeps": [..., "${zone}"] dans taki.app.json apres accord du fondateur (regle G23), ou ne rien poser.`,
    );
  }
  const ids = new Set<string>();
  for (const entree of entrees) {
    if (typeof entree.id !== 'string' || entree.id.trim() === '') {
      refus(appId, zone, 'une entree sans id.');
    }
    if (ids.has(entree.id)) refus(appId, zone, `deux entrees portent l'id "${entree.id}".`);
    ids.add(entree.id);
    if (entree.kind === 'navigation') {
      if (zone !== '3a') refus(appId, zone, 'une navigation se pose dans les actions.');
      for (const nom of ['precedent', 'aujourdHui', 'suivant'] as const) {
        if (typeof entree[nom] !== 'function') refus(appId, zone, `la navigation "${entree.id}" n'a pas de commande ${nom}.`);
      }
      continue;
    }
    if (entree.kind === 'choix') {
      if (zone !== '3a') refus(appId, zone, 'un choix se pose dans les actions.');
      if (typeof entree.onChange !== 'function') refus(appId, zone, `le choix "${entree.id}" n'a pas d'onChange.`);
      const choix = new Set(entree.options.map((option) => option.id));
      if (choix.size !== entree.options.length) refus(appId, zone, `le choix "${entree.id}" contient des ids en double.`);
      // Un catalogue charge ou perd une entree sans effacer le choix courant.
      // Le selecteur natif garde cette valeur dans une option de repli grisee.
      if (typeof entree.value !== 'string') refus(appId, zone, `la valeur du choix "${entree.id}" doit etre une chaine.`);
      continue;
    }
    if (typeof entree.onClick !== 'function') {
      refus(appId, zone, `l'entree "${entree.id}" n'a pas d'onClick : un bouton muet ment.`);
    }
    if (entree.kind !== 'add' && compterLesMots(entree.label) > MOTS_DE_LIBELLE_MAX) {
      refus(
        appId,
        zone,
        `le libelle de "${entree.id}" (« ${entree.label} ») depasse ${MOTS_DE_LIBELLE_MAX} mots (regle G13).`,
      );
    }
  }
  if (zone === '3a') {
    const ajouts = entrees.filter((entree) => entree.kind === 'add');
    if (ajouts.length > 1) {
      refus(appId, zone, `deux actions d'ajout (${ajouts.map((a) => a.id).join(', ')}) : un seul lime par ecran (regle G14).`);
    }
    // Les gestes d'aide colles a droite (`cote: 'droite'`, Support, Exécutions)
    // ne comptent pas dans le plafond : deux au plus, a part.
    const aDroite = entrees.filter((e) => e.kind !== 'navigation' && e.kind !== 'choix' && (e as { cote?: string }).cote === 'droite').length;
    if (aDroite > ACTIONS_A_DROITE_MAX) refus(appId, zone, `${aDroite} actions collees a droite, ${ACTIONS_A_DROITE_MAX} au plus.`);
    const autres = entrees.filter((e) => e.kind !== 'navigation' && e.kind !== 'choix').length - ajouts.length - aDroite;
    if (autres > ACTIONS_DE_SURFACE_MAX) {
      refus(
        appId,
        zone,
        `${autres} actions hors « + Ajouter », ${ACTIONS_DE_SURFACE_MAX} au plus : les gestes rares vont au menu « ... » (3b).`,
      );
    }
  } else {
    const ajout = entrees.find((entree) => entree.kind === 'add');
    if (ajout !== undefined) {
      refus(appId, zone, `"${ajout.id}" est un ajout (kind: 'add') : il se pose en 3a, jamais dans le menu « ... ».`);
    }
  }
  return entrees;
}

/**
 * LE RAIL D'UNE APP CANVAS (zone 1b, retour R3 du fondateur, 06/10/2026) : la
 * LISTE que la surface pose A LA PLACE DES PORTES, au format exact des portes
 * (libelle, compteur gele a droite). Canaux y pose ses conversations groupees
 * (Canaux, Prives, Directs, Lia). Le gabarit
 * rend les entrees par le MEME `RailItem` qu'une porte : la surface ne dessine
 * plus de liste dans sa zone 5 (G23).
 */
function refusDe(appId: string, zone: '1b' | '3b', message: string): never {
  throw new Error(`[app-runtime] ${appId}, ${zone} : ${message}`);
}

/**
 * Verifie le rail pose par une surface : zone 1b gardee, ids uniques et non
 * vides, libelle non vide, `choisir` fourni. Un menu VIDE est toujours
 * recevable (etat de depart).
 */
export function verifierMenuDeSurface(options: {
  appId: string;
  keeps: readonly string[];
  menu: MenuDeSurface | null;
}): MenuDeSurface | null {
  const { appId, keeps, menu } = options;
  if (menu === null || menu.entrees.length === 0) return menu;
  if (!keeps.includes('1b')) {
    refusDe(appId, '1b', 'la fiche ne garde pas 1b (canvas.app.keeps) : le gabarit ne rendrait pas le rail.');
  }
  if (typeof menu.choisir !== 'function') refusDe(appId, '1b', 'poserMenu attend une fonction choisir(id).');
  const ids = new Set<string>();
  for (const entree of menu.entrees) {
    if (typeof entree.id !== 'string' || entree.id.trim() === '') refusDe(appId, '1b', 'une entree sans id.');
    if (ids.has(entree.id)) refusDe(appId, '1b', `deux entrees portent l'id "${entree.id}".`);
    ids.add(entree.id);
    if (typeof entree.libelle !== 'string' || entree.libelle.trim() === '') {
      refusDe(appId, '1b', `l'entree "${entree.id}" n'a pas de libelle.`);
    }
  }
  return menu;
}

/** Verifie les filtres poses : zone 3b gardee, ids uniques, actifs connus. */
export function verifierFiltresDeSurface(options: {
  appId: string;
  keeps: readonly string[];
  filtres: FiltresDeSurface | null;
}): FiltresDeSurface | null {
  const { appId, keeps, filtres } = options;
  if (filtres === null || filtres.catalogue.length === 0) return filtres;
  if (!keeps.includes('3b')) {
    refusDe(appId, '3b', 'la fiche ne garde pas 3b (canvas.app.keeps) : le gabarit ne rendrait pas les filtres.');
  }
  const ids = new Set<string>();
  for (const filtre of filtres.catalogue) {
    if (typeof filtre.id !== 'string' || filtre.id.trim() === '') refusDe(appId, '3b', 'un filtre sans id.');
    if (ids.has(filtre.id)) refusDe(appId, '3b', `deux filtres portent l'id "${filtre.id}".`);
    ids.add(filtre.id);
    if (compterLesMots(filtre.libelle) > MOTS_DE_LIBELLE_MAX) {
      refusDe(appId, '3b', `le libelle du filtre "${filtre.id}" depasse ${MOTS_DE_LIBELLE_MAX} mots (regle G13).`);
    }
  }
  const inconnu = filtres.actifs.find((id) => !ids.has(id));
  if (inconnu !== undefined) refusDe(appId, '3b', `le filtre actif "${inconnu}" n'est pas au catalogue.`);
  return filtres;
}
